import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { TechnicianService } from '../../../core/services/technician.service';
import { AuthService } from '../../../core/services/auth.service';
import { Technician } from '../../../core/models/technician.model';

@Component({
  selector: 'app-technician-list',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './technician-list.html',
  styleUrl: './technician-list.scss',
})
export class TechnicianList {
  private readonly technicianService = inject(TechnicianService);
  private readonly fb = inject(FormBuilder);
  protected readonly authService = inject(AuthService);

  protected readonly technicians = signal<Technician[]>([]);
  protected readonly loading = signal(true);
  protected readonly error = signal<string | null>(null);

  protected readonly modalOpen = signal(false);
  protected readonly saving = signal(false);
  protected readonly editingTechnician = signal<Technician | null>(null);

  protected readonly photoBase64 = signal<string | null>(null);
  protected readonly photoPreviewUrl = computed(() => this.photoBase64() ?? this.editingTechnician()?.photoUrl ?? null);

  protected readonly technicianForm = this.fb.nonNullable.group({
    firstName: ['', Validators.required],
    lastName: [''],
    email: [''],
    phNo: [''],
  });

  constructor() {
    this.loadTechnicians();
  }

  private loadTechnicians(): void {
    this.loading.set(true);
    this.error.set(null);
    this.technicianService.getTechnicians({ pageSize: 200 }).subscribe({
      next: (result) => {
        this.technicians.set(result.items);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set('Failed to load technicians. Is the FieldService API running?');
        this.loading.set(false);
        console.error(err);
      }
    });
  }

  protected openAddModal(): void {
    this.editingTechnician.set(null);
    this.photoBase64.set(null);
    this.technicianForm.reset();
    this.modalOpen.set(true);
  }

  protected openEditModal(technician: Technician): void {
    this.editingTechnician.set(technician);
    this.photoBase64.set(null);
    this.technicianForm.reset({
      firstName: technician.firstName ?? '',
      lastName: technician.lastName ?? '',
      email: technician.email ?? '',
      phNo: technician.phNo ?? '',
    });
    this.modalOpen.set(true);
  }

  protected closeModal(): void {
    this.modalOpen.set(false);
  }

  protected onPhotoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => this.photoBase64.set(reader.result as string);
    reader.readAsDataURL(file);
    input.value = '';
  }

  protected removePhoto(): void {
    this.photoBase64.set(null);
  }

  protected saveTechnician(): void {
    if (this.technicianForm.invalid) {
      this.technicianForm.markAllAsTouched();
      return;
    }

    const value = this.technicianForm.getRawValue();
    const editing = this.editingTechnician();
    const photoBase64 = this.photoBase64() ?? undefined;
    this.saving.set(true);

    if (editing != null) {
      this.technicianService.updateTechnician(editing.technicianId, { ...value, photoBase64, isActive: editing.isActive ?? true }).subscribe({
        next: (updated) => {
          this.technicians.update((list) => list.map((t) => (t.technicianId === updated.technicianId ? updated : t)));
          this.saving.set(false);
          this.closeModal();
        },
        error: (err) => {
          this.saving.set(false);
          this.error.set('Failed to save technician.');
          console.error(err);
        }
      });
    } else {
      this.technicianService.createTechnician({ ...value, photoBase64 }).subscribe({
        next: (created) => {
          this.technicians.update((list) => [...list, created]);
          this.saving.set(false);
          this.closeModal();
        },
        error: (err) => {
          this.saving.set(false);
          this.error.set('Failed to create technician.');
          console.error(err);
        }
      });
    }
  }

  protected deactivateTechnician(technician: Technician): void {
    this.technicianService.deactivateTechnician(technician.technicianId).subscribe({
      next: () => {
        this.technicians.update((list) => list.map((t) => (t.technicianId === technician.technicianId ? { ...t, isActive: false } : t)));
      },
      error: (err) => {
        this.error.set('Failed to deactivate technician.');
        console.error(err);
      }
    });
  }
}
